
import { Options, Vue } from 'vue-class-component';
import { ElectricityEmissionClient, BagEstimationClient, RawMaterialClient } from '@/services/Services';
import MaterialCompositionSummary from '@/views/calculator/components/materialCompositionSummary.vue';
import EditMaterialCompositionModal from '@/views/calculator/modals/editMaterialCompositionModal.vue';
import { openMacroCompositionModal } from '@/views/calculator/components/macroCompositionFill';
import * as OM from '@/Model';
import * as VM from '@/viewModel';
import store from '@/store';

@Options({
    components: {
        MaterialCompositionSummary
    }
})
export default class ComponentsMaterial extends Vue {

    estimationIdentifier: string = "";

    bagData: OM.BagData = new OM.BagData();
    bagComposition: OM.BagSelectedComposition = new OM.BagSelectedComposition();

    allCountries: OM.TextIdentifier[] = [];
    availableMaterials: OM.RawMaterialConfigurationVM[] = [];

    loaded: boolean = false;

    created() {
        this.estimationIdentifier = this.$route.params.estimationIdentifier.toString();
        this.init();
    }

    init() {
        // A Sangle row lives only in the browser until its materials are saved: that save is what makes the
        // server create it. init() replaces bagComposition wholesale, so carry the drafts across, or the
        // grams the user typed vanish when they save anything else.
        let drafts = this.bagComposition.sangleValues.filter(x => !x.materialsAssigned);

        Promise.all([
            BagEstimationClient.getEstimationBagData(this.estimationIdentifier),
            ElectricityEmissionClient.getCountries(false)
        ])
        .then(xs => {
            this.bagData = xs[0];
            this.allCountries = xs[1];

            Promise.all([
                BagEstimationClient.getBagComposition(this.bagData.garment.identifier, this.estimationIdentifier, false, false),
                RawMaterialClient.getAllAvailableMaterialsForGarment(this.bagData.garment.identifier, this.bagData.processType)
            ])
            .then(ys => {
                this.bagComposition = ys[0];
                this.availableMaterials = ys[1];

                drafts
                    .filter(draft => !this.bagComposition.sangleValues.some(x => x.name == draft.name))
                    .forEach(draft => this.bagComposition.sangleValues.push(draft));

                setTimeout(() => {
                    this.loaded = true;
                }, 100);
            })
        })
    }

    get isReadOnly() {
        return store.state.loggedUser.role == VM.Roles.ReadOnly;
    }

    // The counter next to Continue and the Continue gate are the same question, so they are the same
    // computation. A second getter would eventually disagree with this one.
    get pendingCount() {
        let bodyPending = this.bagComposition.percentageCompositionValues
            .filter(x => x.parentName != "")
            .filter(x => !x.materialsAssigned)
            .length;

        // A Sangle with no grams still does not block Continue: the user has not committed to it.
        let sanglePending = this.bagComposition.sangleValues
            .filter(x => x.weight > 0)
            .filter(x => !x.materialsAssigned)
            .length;

        return bodyPending + sanglePending;
    }

    get isDisabled() {
        return this.pendingCount > 0;
    }

    addCompositionToSangle() {
        let nextIndex = this.bagComposition.sangleValues.length + 1;

        let newItem = new OM.PercentageCompositionValues();
        newItem.parentName = "Sangle";
        newItem.name = "Composition part " + nextIndex;
        newItem.weight = 0;

        this.bagComposition.sangleValues.push(newItem);
    }

    openMaterialsModal(row: OM.PercentageCompositionValues, isSangle: boolean) {
        this.$opModal.show(EditMaterialCompositionModal, {
            elementName: row.name,
            availableMaterials: this.availableMaterials,
            allCountries: this.allCountries,
            initialMaterials: row.selectedMaterials,
            initialCountryIdentifier: row.selectedCountry != null ? row.selectedCountry.identifier : "",
            initialNoMaterial: row.materialsAssigned && row.selectedMaterials.length == 0,
            isReadOnly: this.isReadOnly,
            saveCallback: (payload) => {
                let model = new OM.UpdateMaterialCompositionVM();
                model.identifier = this.estimationIdentifier;
                model.name = row.name;
                model.sangleGrams = isSangle ? row.weight : 0;
                model.noMaterial = payload.noMaterial;
                model.materials = payload.noMaterial ? [] : payload.materials;
                model.selectedCountry = payload.noMaterial
                    ? new OM.TextIdentifier()
                    : this.allCountries.filter(x => x.identifier == payload.countryIdentifier)[0];

                let call = isSangle
                    ? BagEstimationClient.updateSangleMaterialComposition(model)
                    : BagEstimationClient.updateMaterialComposition(model);

                return call.then(() => {
                    this.$opModal.closeLast();
                    this.init();
                })
            }
        })
    }

    openMacroModal(macro: OM.PercentageCompositionValues) {
        openMacroCompositionModal(this.$opModal, {
            macro: macro,
            children: this.bagComposition.percentageCompositionValues.filter(x => x.parentName == macro.name),
            availableMaterials: this.availableMaterials,
            allCountries: this.allCountries,
            isReadOnly: this.isReadOnly,
            estimationIdentifier: this.estimationIdentifier,
            save: model => BagEstimationClient.updateMacroMaterialComposition(model),
            onSaved: () => this.init()
        })
    }
}
